// frontend/src/components/MainContent/MainContent.js

import React, { useContext, useState, useRef, useEffect } from 'react';
import { TemplateContext } from "../../context/TemplateContext";
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getPatient } from '../Calendar/calendarApi';
import Timer from '../Timer/Timer';
import TranscriptionArea from '../TranscriptionArea/TranscriptionArea';
import JournalEditor from '../JournalEditor/JournalEditor';
import TopBar from '../TopBar/TopBar';
import ImageGallery from '../ImageGallery/ImageGallery';
import SessionNameField from '../SessionNameField/SessionNameField';
import TranscriptionPendingModal from '../TranscriptionPendingModal/TranscriptionPendingModal';
import PatientSelector from '../PatientSelector/PatientSelector';
import { useRecording } from '../../hooks/useRecording';
import { useAudioStreaming } from '../../hooks/useAudioStreaming';
import { useAudioLevel } from '../../hooks/useAudioLevel';
import { useTranscription } from '../../hooks/useTranscription';
import { useRecordingError } from '../../hooks/useRecordingError';
import { useMicrophones } from '../../hooks/useMicrophones';
import { useSession } from '../../hooks/useSession';
import { useLocalRecording } from '../../hooks/useLocalRecording';
import apiClient, { getChunkStatus, subscribeToSessionStatus } from '../../apiClient';
import streamClient from '../../streamClient';
import './MainContent.css';

const CHUNK_START_AFTER = 30;
const CHUNK_INTERVAL = 30;

const isElectron = typeof window !== 'undefined' && !!window.aionDesktop;

function MainContent() {
  // --- URL query params ---
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionIdFromURL = searchParams.get('session_id') || null;
  const completedParam = searchParams.get('completed') === '1';

  // Nya URL-params för patient-initierad inspelning
  const patientIdFromURL = searchParams.get('patient_id');
  const templateIdFromURL = searchParams.get('template_id');
  const appointmentIdFromURL = searchParams.get('appointment_id');
  const autoRecord = searchParams.get('auto_record') === '1';

  // Hooks
  const recording = useRecording();
  const { transcription, transcriptionRef, isTranscribing, transcribeChunk, resetTranscription, setTranscriptionFromData, appendText } = useTranscription();
  const { error, handleError, clearError } = useRecordingError();
  const { microphones, selectedMicId, setSelectedMicId, captureSystemAudio, setCaptureSystemAudio } = useMicrophones(isElectron);
  const { sessionId, setSessionId, createSession, updateSessionStatus, fetchSessionData } = useSession(sessionIdFromURL, completedParam, 'session');

  // Lokal-först-inspelning: chunkar sparas i IndexedDB och laddas upp i
  // bakgrunden (raderas först vid server-kvittens). Bekräftad transkript-text
  // matas in i transcriptionRef via appendText; server-session-id (som kan komma
  // sent om servern var nere vid start) sätts när uppladdaren skapat sessionen.
  const [clientSessionId, setClientSessionId] = useState(null);
  const {
    beginSession: beginLocalSession,
    attachServerSession,
    enqueueChunk,
    finishSession: finishLocalSession,
    setSessionMeta: setLocalSessionMeta,
    pendingCounts,
  } = useLocalRecording({
    onConfirmedText: (ev) => { if (ev.text) appendText(ev.text); },
    onServerSession: ({ serverSessionId }) => { if (serverSessionId) setSessionId(serverSessionId); },
    onSessionCompleted: ({ serverSessionId }) => { if (serverSessionId) setSessionId(serverSessionId); },
  });
  // Lokala, ännu ej bekräftade chunkar för aktiv session (blockerar journal).
  const localPendingForSession = clientSessionId
    ? (pendingCounts[clientSessionId]?.pending || 0)
    : 0;

  // (Effekten som synkar lokal sessions mall/namn är flyttad längre ned — den
  //  beror på selectedTemplate/templateId/sessionName som deklareras senare.)

  // Routar en chunk rätt: nya inspelningar går via lokal-först-kön (enqueueChunk);
  // en återupptagen BEFINTLIG server-session (öppnad från Mina sessioner) saknar
  // lokal session och laddas upp direkt mot sitt kända server-id (som förut) —
  // detta undviker både datatapp och chunk_index-krock mot redan mottagna chunkar.
  const handleChunk = async (blob, isFinal = false) => {
    if (!blob) return;
    if (clientSessionId) {
      await enqueueChunk(blob, isFinal);
    } else if (sessionId) {
      await transcribeChunk(blob, sessionId, '/recorder/sessions', isFinal);
    }
  };
  
  const { soundLevel, noAudioDetected } = useAudioLevel({
    isRecording: recording.isRecording,
    isPaused: recording.isPaused,
    selectedMicId,
  });

  const { transcriptionAreaRef } = useAudioStreaming({
    currentTime: recording.currentTime,
    isRecording: recording.isRecording,
    isPaused: recording.isPaused,
    chunkStartAfter: CHUNK_START_AFTER,
    chunkInterval: CHUNK_INTERVAL
  });

  // GPT-sektioner
  const [streamingSections, setStreamingSections] = useState([]);
  const [generatedSections, setGeneratedSections] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isStopping, setIsStopping] = useState(false);

  // Templates
  const { templateId, setTemplateId } = useContext(TemplateContext);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const templateIdRef = useRef(null);
  useEffect(() => { templateIdRef.current = templateId; }, [templateId]);
  // Alltid färsk referens till vald mall. Sätts även SYNKRONT vid mallbyte
  // (se handleChangeTemplate) så att regenerering aldrig kan läsa en inaktuell
  // selectedTemplate och skicka fel template_id till backend.
  const selectedTemplateRef = useRef(null);
  useEffect(() => { selectedTemplateRef.current = selectedTemplate; }, [selectedTemplate]);
  // Vid mallbyte: håller mall-id:t vi vill regenerera mot. Regenereringen körs
  // i en effekt FÖRST när selectedTemplate hunnit uppdateras till den nya mallen
  // (samma väg som knappen "Gör om journal", som bevisligen fungerar).
  const [regenerateForTemplateId, setRegenerateForTemplateId] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  // Visa JournalEditor eller ej?
  const [showJournal, setShowJournal] = useState(false);
  const [transcriptionUpdatedAfterJournal, setTranscriptionUpdatedAfterJournal] = useState(false);

  // Loading overlay för journal generation
  const [showGenerationOverlay, setShowGenerationOverlay] = useState(false);
  const [generationMessage, setGenerationMessage] = useState('');

  // Humla ImageGallery state
  const [showImageGallery, setShowImageGallery] = useState(false);

  // Session name state
  const [sessionName, setSessionName] = useState('');

  // Håll den lokala sessionens mall/namn färska om användaren väljer mall eller
  // namnger sessionen EFTER inspelningsstart (så en lazy-skapad server-session
  // får rätt mall även när servern var nere vid start). Placerad EFTER
  // selectedTemplate/templateId/sessionName så deras deklarationer hunnit köra.
  useEffect(() => {
    if (!clientSessionId) return;
    const tid = selectedTemplate?.template_id || templateId || null;
    setLocalSessionMeta({ templateId: tid, sessionName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSessionId, selectedTemplate, templateId, sessionName]);

  // Pending journal text from loaded session (väntar på template)
  const [pendingJournalText, setPendingJournalText] = useState(null);

  // Pending transcription modal state
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [backgroundRetryActive, setBackgroundRetryActive] = useState(false);
  // Lokal-först: sant när servern var nere så länge att inspelningen ännu inte
  // kunnat skickas — den ligger säkert lokalt och skickas automatiskt.
  const [localDeferredNotice, setLocalDeferredNotice] = useState(false);
  const retryCleanupRef = useRef(null);

  // Klinik-inställningar för patientjournal
  const [clinicSettings, setClinicSettings] = useState({
    use_internal_journal: false,
    journal_system: 'Kaddio'
  });
  const [selectedPatient, setSelectedPatient] = useState(null);

  // State för kopplad bokning (från URL)
  const [linkedAppointmentId, setLinkedAppointmentId] = useState(null);

  // Ref för att förhindra att auto-record körs flera gånger
  const autoRecordStartedRef = useRef(false);

  // ===========================================================
  //        Hämta klinik-inställningar
  // ===========================================================
  useEffect(() => {
    const fetchClinicSettings = async () => {
      try {
        const resp = await apiClient.get('/api/journal/clinic/settings');
        setClinicSettings({
          use_internal_journal: resp.data.use_internal_journal || false,
          journal_system: resp.data.journal_system || 'Kaddio'
        });
      } catch (error) {
        console.error('Error fetching clinic settings:', error);
        // Använd defaults om hämtningen misslyckas
      }
    };

    fetchClinicSettings();
  }, []);

  // ===========================================================
  //        Ladda patient från URL (för patient-initierad inspelning)
  // ===========================================================
  useEffect(() => {
    const loadPatientFromURL = async () => {
      if (patientIdFromURL && !selectedPatient) {
        try {
          const patient = await getPatient(patientIdFromURL);
          setSelectedPatient(patient);

          // Sätt appointment_id om det skickades med
          if (appointmentIdFromURL) {
            setLinkedAppointmentId(parseInt(appointmentIdFromURL));
          }

          console.log('Patient loaded from URL:', patient.firstname, patient.lastname);
        } catch (error) {
          console.error('Error loading patient from URL:', error);
        }
      }
    };

    loadPatientFromURL();
  }, [patientIdFromURL, appointmentIdFromURL]);

  // ===========================================================
  //        Hämta mallar
  // ===========================================================
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const resp = await apiClient.get('/api/journalmallar');

        if (Array.isArray(resp.data)) {
          setTemplates(resp.data);

          // Om templateId redan finns (t.ex. från session) → välj den
          if (templateId) {
            const found = resp.data.find(t => t.template_id === templateId);
            if (found) setSelectedTemplate(found);
          }

          // Om templateId saknas (ny session) → välj första och sätt context
          if (!templateId && resp.data.length > 0) {
            setTemplateId(resp.data[0].template_id);
            setSelectedTemplate(resp.data[0]);
          }
        } else {
          handleError('Fel vid hämtning av mallar.');
        }

        setLoadingTemplates(false);
      } catch (err) {
        handleError('Kunde inte hämta mallar', err);
        setLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, []);


  // ===========================================================
  //        Hämta standard journalmall
  // ===========================================================

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const resp = await apiClient.get('/auth/user'); // Anpassa om du har annan endpoint
        const user = resp.data;
        if (!templateIdRef.current && user?.default_journaltemplate_id && templates.length > 0) {
          const match = templates.find(t => t.template_id === user.default_journaltemplate_id);
          if (match) {
            setTemplateId(match.template_id);       // ⬅ sätter context
            setSelectedTemplate(match);             // ⬅ sätter lokalt
          }
        }
      } catch (err) {
        console.error('Kunde inte hämta användarprofil:', err);
      }
    };

    if (templates.length > 0) {
      fetchUserProfile();
    }
  }, [templates]);

  // ===========================================================
  //     Synka templateId (context) → selectedTemplate
  // ===========================================================
  useEffect(() => {
    if (!templateId || templates.length === 0) return;
    const found = templates.find(t => t.template_id === templateId);
    if (found) setSelectedTemplate(found);
  }, [templateId, templates]);

  // ===========================================================
  //     Sätt template från URL (för patient-initierad inspelning)
  // ===========================================================
  useEffect(() => {
    if (templateIdFromURL && templates.length > 0 && !sessionIdFromURL) {
      const urlTemplateId = parseInt(templateIdFromURL);
      const found = templates.find(t => t.template_id === urlTemplateId);
      if (found && found.template_id !== selectedTemplate?.template_id) {
        setTemplateId(urlTemplateId);
        setSelectedTemplate(found);
        console.log('Template set from URL:', found.template_name);
      }
    }
  }, [templateIdFromURL, templates, sessionIdFromURL]);

  // ===========================================================
  //     Auto-starta inspelning (för patient-initierad inspelning)
  // ===========================================================
  useEffect(() => {
    // Kontrollera alla förutsättningar för auto-start
    const canAutoStart = autoRecord &&
      !autoRecordStartedRef.current &&
      !recording.isRecording &&
      !sessionId &&
      selectedTemplate &&
      selectedPatient;

    if (canAutoStart) {
      // Markera att vi redan startat för att undvika dubbel-start
      autoRecordStartedRef.current = true;
      console.log('Auto-starting recording for patient:', selectedPatient.firstname, selectedPatient.lastname);
      startRecording();
    }
  }, [autoRecord, recording.isRecording, sessionId, selectedTemplate, selectedPatient]);

  // ===========================================================
  //        Hämta session-data om sessionId finns
  // ===========================================================
  useEffect(() => {
    if (sessionIdFromURL) {
      handleFetchSessionData(sessionIdFromURL);
    }
    // eslint-disable-next-line
  }, [sessionIdFromURL]);

  // ===========================================================
  //   Parsa pending journal när template är redo
  // ===========================================================
  useEffect(() => {
    // Vänta tills både journaltext och template finns
    if (!pendingJournalText || !selectedTemplate) return;

    // VIKTIGT: selectedTemplate synkas från templateId i en separat effekt (en
    // render senare). Vid laddning av session sätts templateId + pendingJournalText
    // i samma batch, så vid första rendret är selectedTemplate fortfarande den
    // gamla (standard)mallen. Parsar vi då hamnar sessionens rubriker utanför
    // allowedTitles och klistras ihop under en rubrik. Vänta tills selectedTemplate
    // faktiskt motsvarar aktuellt templateId innan vi parsar.
    if (templateId && selectedTemplate.template_id !== templateId) return;

    console.log('Parsing pending journal with template:', selectedTemplate.template_name);
    const secs = parseJournalNote(pendingJournalText);
    setGeneratedSections(secs);

    // Rensa pending text så vi inte parsar igen
    setPendingJournalText(null);
  }, [pendingJournalText, selectedTemplate, templateId]);

  const formatDateTime = (date) => {
    if (!date) return '';
    return date.toLocaleString('sv-SE');
  };

  const handleFetchSessionData = async (sid) => {
    console.log('handleFetchSessionData called with sessionId:', sid);
    try {
      const sessionData = await fetchSessionData(sid);
      const {
        status,
        transcription_text,
        generated_journal,
        start_time,
        elapsed_time,
        template_id,
        session_name,
        transcription_updated_after_journal,
      } = sessionData;

      // Set session name if available
      if (session_name) {
        setSessionName(session_name);
      }
      
      console.log('Session data fetched:', {
        status, 
        elapsed_time, 
        transcription_text: transcription_text?.length,
        template_id,
        generated_journal: generated_journal?.length
      });


      if (template_id) {
        setTemplateId(template_id);
      }

      setTranscriptionFromData(transcription_text);

      if (start_time && !recording.startDateTime) {
        recording.setStartDateTime(new Date(start_time));
      }

      if (status === 'completed' || completedParam) {
        if (elapsed_time) {
          // elapsed_time från backend är i sekunder
          recording.setElapsedTimeFromBackend(elapsed_time);
        }
        if (generated_journal) {
          // Spara journaltext och vänta på att template laddas innan vi parsar
          setPendingJournalText(generated_journal);
        }
        setTranscriptionUpdatedAfterJournal(transcription_updated_after_journal || false);
        setShowJournal(true);
        recording.stop();
      } else if (status === 'paused') {
        setShowJournal(false);
        console.log('Setting paused state with elapsed_time:', elapsed_time);
        // Sätt recording state till pausad med elapsed time
        recording.setPausedState(elapsed_time);
      } else if (status === 'in_progress') {
        setShowJournal(false);
        if (elapsed_time) {
          // elapsed_time från backend är i sekunder
          recording.setElapsedTimeFromBackend(elapsed_time);
        }
        // Session är aktiv men sidan laddades om - starta inspelningen
        recording.start();
      }
    } catch (err) {
      handleError('Kunde inte hämta session', err);
    }
  };

  // ===========================================================
  //        Spara generated_journal
  // ===========================================================
  const saveGeneratedJournal = async (journalText) => {
    if (!sessionId) return;
    try {
      await apiClient.patch(`/recorder/sessions/${sessionId}/update_journal`, {
        generated_journal: journalText,
      });
      console.log('Journal sparad framgångsrikt!');
    } catch (err) {
      console.error('Kunde inte spara journal:', err);
    }
  };

  // ===========================================================
  //   Hantera patientväljare
  // ===========================================================
  // Patientvalet lever enbart i frontend-state — inspelningssessionen (Notes)
  // känner inte till patienten. Kopplingen sparas i patient_journal_entries
  // när journalen sparas till patient.
  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
  };

  const handlePatientClear = () => {
    setSelectedPatient(null);
  };

  // ===========================================================
  //   Auto-pausa om man lämnar sidan
  // ===========================================================
  useEffect(() => {
    return () => {
      if (recording.isRecording && !recording.isPaused) {
        pauseRecording();
      }
      // Rensa retry-polling vid unmount
      if (retryCleanupRef.current) {
        retryCleanupRef.current();
        retryCleanupRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [recording.isRecording, recording.isPaused]);

// ===========================================================
//              Inspelningslogik
// ===========================================================
const startRecording = async () => {
  clearError();
  setShowJournal(false);

  if (!sessionId) {
    // Helt ny session - starta från början
    recording.start();
    resetTranscription();
    setStreamingSections([]);
    setGeneratedSections([]);

    // 1) Skapa ALLTID en lokal session först — kräver ingen server och kan aldrig
    //    misslyckas pga nedgång. Inspelningen är därmed säkrad från första sekunden.
    const chosenTemplateId = templateId || selectedTemplate?.template_id;
    let cs = null;
    try {
      cs = await beginLocalSession({ templateId: chosenTemplateId, sessionName });
      setClientSessionId(cs);
    } catch (err) {
      // IndexedDB kan i extremfall saknas — logga men fortsätt (server-create nedan).
      console.error('Kunde inte skapa lokal session:', err);
    }

    // 2) Försök skapa server-sessionen — men blockera INTE inspelningen om det
    //    misslyckas (servern kan vara nere). Chunkar buffras lokalt och server-
    //    sessionen skapas då lazy vid första lyckade upload.
    //    Skicka client_session_id så server-sessionen länkas till klientens id —
    //    annars hittar inte chunk-uploaderna sessionen och skapar en dubblett.
    try {
      const newSessionId = await createSession(chosenTemplateId, '/recorder/sessions/start', null, cs);
      setSessionId(newSessionId);
      await attachServerSession(newSessionId);
    } catch (err) {
      console.warn('Server-session kunde inte skapas vid start (fortsätter lokalt):', err);
    }
  } else {
    // Befintlig session - ska egentligen inte komma hit om användaren pausat
    // Detta kan bara hända om sidan laddats om eller liknande
    console.log('Starting with existing session - this should not happen if user paused');
    recording.start();
  }

  if (transcriptionAreaRef.current) {
    transcriptionAreaRef.current.startRecording();
  }
};

  const pauseRecording = async () => {
    if (!recording.isRecording || recording.isPaused) return;
    
    const elapsedBeforePause = recording.getElapsedTime();
    console.log('Pausing recording with elapsed time:', elapsedBeforePause, 'ms');
    
    recording.pause();

    if (transcriptionAreaRef.current) {
      const blob = await transcriptionAreaRef.current.stopRecording();
      if (blob) {
        await handleChunk(blob, false);
      }
    }

    if (sessionId) {
      try {
        await updateSessionStatus('pause', elapsedBeforePause);
      } catch (err) {
        handleError('Kunde inte pausa session', err);
      }
    }
  };

  const resumeRecording = async () => {
    if (!recording.isPaused) return;
    
    
    recording.resume();

    if (transcriptionAreaRef.current) {
      transcriptionAreaRef.current.startRecording();
    }

    if (sessionId) {
      try {
        await updateSessionStatus('resume');
      } catch (err) {
        handleError('Kunde inte återuppta session', err);
      }
    }
  };


  const stopRecording = async () => {
    if (isStopping) {
      console.log('Already stopping, ignoring duplicate call');
      return;
    }

    setIsStopping(true);
    recording.stop();

    if (!selectedTemplate) {
      alert('Du måste välja en mall innan du avslutar inspelningen.');
      setIsStopping(false);
      return;
    }

    // 1) Sista chunk → lokal kö (markera final för finish-prioritet server-sidan)
    if (transcriptionAreaRef.current) {
      const blob = await transcriptionAreaRef.current.stopRecording();
      if (blob) {
        await handleChunk(blob, true);
      }
    }

    // 2) Markera klar lokalt (endast lokal-först-sessioner). Uppladdaren kör
    //    server-`complete` när den lokala kön är tömd (så servern aldrig avslutar
    //    med ett hål i transkriptet). Återupptagna legacy-sessioner completas nedan.
    if (clientSessionId) {
      await finishLocalSession();
    }

    // 3) Registrera varaktighet/complete server-sidan om sessionen redan finns.
    //    Sena chunkar accepteras på en completed session. Saknas sessionen (server
    //    var nere vid start) sköter uppladdaren complete lazy när den kommit upp.
    if (sessionId) {
      try {
        await updateSessionStatus('complete', recording.getElapsedTime());
      } catch (err) {
        console.warn('complete_session kunde inte köras nu (uppladdaren gör det senare):', err);
      }
    }

    // 4) Vänta in lokal kö + server-processering i pending-modalen. Journalen
    //    genereras först när ALLT bekräftats (transcriptionRef är då komplett).
    setShowPendingModal(true);
  };

  // Helper-funktion för journal-generering
  const proceedWithJournalGeneration = async () => {
    // Visa loading overlay
    setShowGenerationOverlay(true);
    setGenerationMessage('Aion genererar din journal...');

    // Visa JournalEditor direkt
    setShowJournal(true);

    // Kör GPT-generering (stream i realtid)
    try {
      const finalGPTtext = await generateJournalStreaming(
        transcriptionRef.current.trim(),
        selectedTemplateRef.current?.template_id ?? selectedTemplate?.template_id
      );
      if (finalGPTtext) {
        await saveGeneratedJournal(finalGPTtext);
      }
    } catch (err) {
      handleError('Fel vid GPT-generering efter stopp', err);
    } finally {
      setIsStopping(false);
    }
  };

  // Callback när pending modal är klar
  const handlePendingComplete = async (result) => {
    console.log('Active chunks completed, proceeding with journal generation', result);
    setShowPendingModal(false);

    // Servern var ihållande nere: lokala chunkar väntar fortfarande. Generera
    // INTE en halv journal — inspelningen är säkrad lokalt och skickas automatiskt
    // när servern är tillbaka. Visa lugnande info i stället.
    if (result?.deferredLocal) {
      setLocalDeferredNotice(true);
      setIsStopping(false);
      return;
    }

    // Om det finns retries i bakgrunden - starta polling
    if (result?.hasMorePending && sessionId) {
      setBackgroundRetryActive(true);

      // Rensa eventuell tidigare polling
      if (retryCleanupRef.current) retryCleanupRef.current();

      retryCleanupRef.current = subscribeToSessionStatus(sessionId, (status) => {
        if (!status.has_pending_retries) {
          setBackgroundRetryActive(false);
          console.log('Background retries completed, refreshing session data');
          fetchSessionData(sessionId);
          if (retryCleanupRef.current) {
            retryCleanupRef.current();
            retryCleanupRef.current = null;
          }
        }
      });
    }

    await proceedWithJournalGeneration();
  };


  // ===========================================================
  //         Fake UX Streaming Helper
  // ===========================================================
  const animateSectionsAppearance = async (sections, setSections) => {
    /**
     * Visar sektioner en i taget med delay för bättre UX
     * Även om backend returnerar allt på en gång
     */
    const DELAY_BETWEEN_SECTIONS = 400; // ms mellan varje sektion

    for (let i = 0; i < sections.length; i++) {
      // Visa sektioner progressivt
      setSections(sections.slice(0, i + 1));

      // Vänta innan nästa sektion (skippa delay efter sista)
      if (i < sections.length - 1) {
        await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_SECTIONS));
      }
    }
  };

  // ===========================================================
  //         parseJournalNote (SIMPLIFIED - trusts backend)
  // ===========================================================
  const parseJournalNote = (text, template = null) => {
    /**
     * @param template - Optional template override (fixes stale closure when switching templates)
     */

    if (!text || !text.trim()) {
      return [];
    }

    const templateToUse = template || selectedTemplate;

    // Hämta tillåtna rubriker från template (för parsing)
    const allowedTitles = new Set();
    if (templateToUse?.sections) {
      templateToUse.sections.forEach(sec => {
        allowedTitles.add(sec.section_title.trim());
      });
    }

    const lines = text.split('\n');
    const secs = [];
    let current = null;
    let i = 0;

    while (i < lines.length) {
      const line = lines[i].trim();

      if (line) {
        // Är detta en giltig rubrik?
        const isValidTitle = allowedTitles.size === 0 || allowedTitles.has(line);

        if (isValidTitle && !current) {
          // Starta ny sektion
          current = { title: line, content: '' };
          i++;

          // Hoppa över tomma rader efter rubrik
          while (i < lines.length && lines[i]?.trim() === '') i++;

          // Samla innehåll tills nästa rubrik eller tom rad
          while (i < lines.length) {
            const nextLine = lines[i].trim();

            // Ny rubrik? Avbryt (konsekvent med isValidTitle-logiken ovan)
            const isNextValidTitle = allowedTitles.size === 0 || allowedTitles.has(nextLine);
            if (nextLine && isNextValidTitle) {
              break;
            }

            // Tom rad efter innehåll? Avbryt
            if (!nextLine && current.content.trim()) {
              i++;
              break;
            }

            current.content += lines[i] + '\n';
            i++;
          }

          secs.push(current);
          current = null;
        } else if (current) {
          // Lägg till i nuvarande sektion
          current.content += line + '\n';
          i++;
        } else {
          // Text utan rubrik - lägg i "Okänt" eller senaste sektion
          if (secs.length === 0) {
            secs.push({ title: 'Okänt', content: line + '\n' });
          } else {
            secs[secs.length - 1].content += line + '\n';
          }
          i++;
        }
      } else {
        i++;
      }
    }

    return secs;
  };

  const generateJournalStreaming = async (text, templateId, templateOverride = null) => {
    /**
     * SIMPLIFIED med FAKE UX STREAMING
     * Backend returnerar korrekt data direkt
     * Frontend visar sektioner progressivt för bättre UX
     *
     * @param templateOverride - Optional template object to use for parsing (fixes stale closure on template switch)
     */

    if (!templateId) {
      handleError('Ingen mall vald');
      return '';
    }

    // Härled ALLTID parsnings-mallen från samma template_id som skickas till
    // backend. Annars kan backend generera mot en mall medan frontend parsar
    // mot en annan (stale selectedTemplate) → rubriker matchar inte och viks
    // ihop under en rubrik.
    const templateForParse =
      templateOverride ||
      templates.find((t) => t.template_id === templateId) ||
      selectedTemplateRef.current ||
      selectedTemplate;

    console.log('[generateJournal] template_id sent:', templateId,
      '| parse-mall:', templateForParse?.template_name);

    setStreamingSections([]);
    setGeneratedSections([]);
    setIsGenerating(true);
    clearError();

    let accumulated = '';

    try {
      // Backend streaming (samlar hela svaret)
      await streamClient.post(
        '/api/chatgpt/generate_journal',
        { text, template_id: templateId, session_id: sessionId },
        (chunk) => {
          accumulated += chunk;
          const partial = parseJournalNote(accumulated, templateForParse);
          setStreamingSections(partial);
        }
      );

      // Parse final result
      const finalSecs = parseJournalNote(accumulated.trim(), templateForParse);

      // FAKE UX STREAMING: Visa sektioner en i taget
      setStreamingSections([]);
      await animateSectionsAppearance(finalSecs, setStreamingSections);

      // När animationen är klar
      setGeneratedSections(finalSecs);
      setStreamingSections([]);
      setIsGenerating(false);

      const gptText = finalSecs
        .map((sec) => `${sec.title}\n${sec.content.trim()}`)
        .join('\n\n');

      return gptText;
    } catch (err) {
      handleError('Fel vid GPT-generering', err);
      setIsGenerating(false);
      return '';
    } finally {
      // Dölj loading overlay när genereringen är klar
      setShowGenerationOverlay(false);
    }
  };

  // ===========================================================
  //       JournalEditor-callbacks
  // ===========================================================
  const handleRegenerate = async () => {
    const combinedText = transcriptionRef.current.trim();

    // generateJournalStreaming hanterar nu fake UX streaming internt.
    // Läs mall-id från ref:en (alltid färsk) så att en regenerering direkt
    // efter ett mallbyte inte skickar gammalt template_id.
    const gptText = await generateJournalStreaming(
      combinedText,
      selectedTemplateRef.current?.template_id ?? selectedTemplate?.template_id
    );

    if (gptText && sessionId) {
      await saveGeneratedJournal(gptText);
    }
  };

  const handleUpdateGeneratedSections = (newSections) => {
    setGeneratedSections(newSections);
  };

  const handleChangeTemplate = async (newTemplateId) => {
    const found = templates.find((t) => t.template_id === newTemplateId);
    if (!found) {
      handleError('Mall ej hittad.');
      return;
    }

    // Rensa gamla sektioner INNAN mallbyte, annars kör useJournalSections
    // prepareSections(gamla sektioner, ny mall) och manglar rubrikerna
    setGeneratedSections([]);
    // Synkron uppdatering av ref:en så att ev. regenerering som triggas innan
    // setSelectedTemplate hunnit commit:a ändå läser den NYA mallen (annars
    // skickas gamla template_id till generate_journal → fel mall genereras).
    selectedTemplateRef.current = found;
    setSelectedTemplate(found);
    setTemplateId(newTemplateId);

    if (sessionId) {
       await apiClient.patch(
         `/recorder/sessions/${sessionId}/update_template`,
         { template_id: newTemplateId }
       );
    }
    // Endast om vi är i Journal-läge vill vi regenerera. Vi gör det INTE direkt
    // här (då hinner inte selectedTemplate uppdateras → fel mall genereras).
    // I stället flaggar vi för regenerering som körs i effekten nedan när
    // selectedTemplate faktiskt blivit den nya mallen — exakt som att manuellt
    // trycka "Gör om journal" efter bytet.
    if (showJournal) {
      setRegenerateForTemplateId(newTemplateId);
    }
  };

  // Kör regenereringen efter mallbyte – först när selectedTemplate hunnit bli
  // den nya mallen. Då använder handleRegenerate rätt mall (samma som knappen).
  useEffect(() => {
    if (regenerateForTemplateId == null) return;
    if (selectedTemplate?.template_id !== regenerateForTemplateId) return;
    setRegenerateForTemplateId(null);
    handleRegenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regenerateForTemplateId, selectedTemplate]);

  const handleAdjust = async (instruction) => {
    if (generatedSections.length === 0) {
      handleError('Inga sektioner att justera.');
      return;
    }
    try {
      const resp = await apiClient.post('/api/journal/adjust', {
        instruction,
        sections: generatedSections.map((sec) => ({
          title: sec.title,
          content: sec.content,
        })),
      });
      if (resp.data.sections && resp.data.sections.length > 0) {
        setGeneratedSections(resp.data.sections);

        const combinedText = resp.data.sections
          .map((s) => `${s.title}\n${s.content.trim()}`)
          .join('\n\n');

        if (sessionId) {
          await saveGeneratedJournal(combinedText);
        }
      } else {
        handleError('Justering returnerade inga sektioner.');
      }
    } catch (err) {
      handleError('Fel vid justering av journal', err);
    }
  };


  // ===========================================================
  //                Render
  // ===========================================================

  // 1) Om showJournal => visa JournalEditor
  if (showJournal) {
    return (
      <JournalEditor
        sessionId={sessionId}
        inputText={transcriptionRef.current}
        selectedTemplate={selectedTemplate}
        templates={templates}
        streamingSections={streamingSections}
        generatedSections={generatedSections}
        isTranscribing={isGenerating}
        onRegenerate={handleRegenerate}
        onChangeTemplate={handleChangeTemplate}
        onAdjust={handleAdjust}
        onUpdateGeneratedSections={handleUpdateGeneratedSections}
        startDateTime={recording.startDateTime}
        sessionName={sessionName}
        onSessionNameChange={setSessionName}
        // Props för patientjournal-integration
        patientId={selectedPatient?.patient_id || null}
        selectedPatient={selectedPatient}
        onPatientChange={setSelectedPatient}
        appointmentId={linkedAppointmentId}
        useInternalJournal={clinicSettings.use_internal_journal}
        transcriptionUpdatedAfterJournal={transcriptionUpdatedAfterJournal}
        onJournalSaved={(entryId) => {
          console.log('Journal saved with entry_id:', entryId);
          // Om vi kom från patientvyn - navigera tillbaka till patientens journal
          if (selectedPatient?.patient_id) {
            navigate(`/patients/${selectedPatient.patient_id}?tab=journal`);
          }
        }}
      />
    );
  }

  // 2) Annars inspelningsvyn
return (
  <>
    <TopBar
      templates={templates}
      selectedTemplate={selectedTemplate}
      loadingTemplates={loadingTemplates}
      onTemplateChange={handleChangeTemplate}
      showTabs={false}
      showStopButton={recording.isRecording || recording.isPaused}
      onStopRecording={stopRecording}
      startDateTime={recording.startDateTime}
      microphones={microphones}
      selectedMicId={selectedMicId}
      onChangeMic={(micId) => setSelectedMicId(micId)}
      captureSystemAudio={captureSystemAudio}
      onToggleSystemAudio={() => setCaptureSystemAudio((prev) => !prev)}
      hasRecordingError={!!error}
    />

    <div className="main-content-container">
      <div className="main-content">
        <div className="recording-wrapper">
          {/* Session Name Field - visible when session exists, above header */}
          {sessionId && (
            <SessionNameField
              sessionId={sessionId}
              initialName={sessionName}
              onNameChange={setSessionName}
              placeholder="Namnge journalen..."
              className="main-content-session-name"
            />
          )}

          <h2 className="main-header">Nytt Patientsamtal</h2>

          {/* Patientväljare - visas endast om kliniken använder inbyggd journal */}
          {clinicSettings.use_internal_journal && (
            <div className="main-content-patient-selector">
              <div className="patient-selector-wrapper">
                <label>Koppla till patient (valfritt)</label>
                <PatientSelector
                  selectedPatient={selectedPatient}
                  onPatientSelect={handlePatientSelect}
                  onClear={handlePatientClear}
                  placeholder="Sök patient..."
                  disabled={false}
                />
              </div>
            </div>
          )}

          {recording.startDateTime && (
            <div className="start-date-time">
              Startat: {formatDateTime(recording.startDateTime)}
            </div>
          )}

          <Timer currentTime={recording.currentTime} />

          <div className="central-record-button-container">
            <button
              className={`central-record-button elongated ${recording.isRecording ? 'recording' : ''}`}
              style={{ '--sound-level': soundLevel }}
              onClick={() => {
                if (!recording.isRecording && !recording.isPaused) {
                  startRecording();
                } else if (recording.isRecording && !recording.isPaused) {
                  pauseRecording();
                } else if (recording.isPaused) {
                  resumeRecording();
                }
              }}
            >
              {!recording.isRecording && !recording.isPaused
                ? 'Starta inspelning'
                : recording.isRecording && !recording.isPaused
                ? 'Pausa inspelning'
                : 'Återuppta inspelning'}
            </button>

            {recording.isRecording && !recording.isPaused && noAudioDetected && (
              <div className="no-audio-warning" role="status">
                Inget ljud detekteras — talar du, och är rätt mikrofon vald?
              </div>
            )}
          </div>

          {/* Humla Image Gallery Button */}
          {sessionId && (
            <div className="image-gallery-button-container" style={{ marginTop: '1.5rem', textAlign: 'center' }}>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => setShowImageGallery(true)}
                title="Visa bilder från Humla"
              >
                🖼️ Bilder från Humla
              </button>
            </div>
          )}
        </div>

        {/* Om du vill visa rullande transkriberings-text i UI, avkommentera detta block:
        <div className="transcription-preview" style={{ marginTop: '2rem', padding: '1rem', background: '#f7f7f7' }}>
          <h3>Rullande transkribering (chunk-baserad):</h3>
          {isTranscribing && <p style={{ color: 'gray' }}>Transkriberar chunk...</p>}
          {transcription ? (
            <p>{transcription}</p>
          ) : (
            <p style={{ fontStyle: 'italic', color: '#666' }}>
              Ingen transkription hittills. Prata för att se text här.
            </p>
          )}
        </div>
        */}

        <div style={{ display: 'none' }}>
          <TranscriptionArea
            ref={transcriptionAreaRef}
            onDataAvailable={(chunk) => handleChunk(chunk, false)}
            selectedMicId={selectedMicId}
            captureSystemAudio={captureSystemAudio}
          />
        </div>

        {error && <p className="error-message">{error}</p>}

        {backgroundRetryActive && (
          <div className="alert alert-info mt-2 mb-0 py-2 px-3" style={{ fontSize: '0.85rem' }}>
            Vissa delar av inspelningen transkriberas fortfarande i bakgrunden. Journalen uppdateras automatiskt.
          </div>
        )}

        {localDeferredNotice && (
          <div className="alert alert-warning mt-2 mb-0 py-2 px-3" style={{ fontSize: '0.85rem' }}>
            Inspelningen är sparad säkert på den här datorn och skickas automatiskt
            så fort servern går att nå igen — journalen skapas då. Stäng inte
            webbläsaren helt förrän den skickats klart. Du hittar den under Mina
            sessioner (markerad som ej skickad).
          </div>
        )}
      </div>
    </div>

    {/* Loading overlay för journal generation */}
    {showGenerationOverlay && (
      <div className="journal-generation-overlay">
        <div className="loading-spinner"></div>
        <p className="loading-text">{generationMessage}</p>
      </div>
    )}

    {/* Humla Image Gallery */}
    <ImageGallery
      isOpen={showImageGallery}
      onClose={() => setShowImageGallery(false)}
      sessionId={sessionId}
      autoRefresh={false}
    />

    {/* Pending Transcription Modal */}
    {showPendingModal && (
      <TranscriptionPendingModal
        sessionId={sessionId}
        localPending={localPendingForSession}
        onComplete={handlePendingComplete}
      />
    )}
  </>
);
}

export default MainContent;
